import { flatMap, property } from 'lodash';
import { handleActions } from 'redux-actions';
import update from 'immutability-helper';
import { createSelector } from 'reselect';

import Constants from 'constants/actions';

const initialState = {
  rules: [],
  items: [],
  isReceived: false,
};

export const getItems = state => state.items || [];

export const getItemIds = state => getItems(state).map(item => item.id);

export const getItem = (state, itemId) =>
  getItems(state).find(item => item.id === itemId) || {};

export const isItemExpanded = (state, itemId) =>
  getItem(state, itemId).isExpanded;

export const getItemIndex = (state, itemId) =>
  getItems(state).findIndex(item => item.id === itemId);

export const getRules = (state, itemId) => getItem(state, itemId).rules || [];

export const getPersistedRules = createSelector(getRules, rules =>
  rules.filter(rule => !!rule.id),
);

export const getPersistedRulesCount = (state, itemId) =>
  getPersistedRules(state, itemId).length;

export const getAllRules = state => flatMap(getItems(state), property('rules'));

export const getRule = (state, clientId) =>
  getAllRules(state).find(rule => rule.clientId === clientId);

export const getRuleIndex = (state, itemId, clientId) =>
  getRules(state, itemId).findIndex(rule => rule.clientId === clientId);

export const getRuleClientIds = (state, itemId) =>
  getRules(state, itemId).map(rule => rule.clientId);

export const getQuestionId = (state, itemId) =>
  getItem(state, itemId).questionId;

export const getRulePersistenceId = (state, clientId) =>
  getRule(state, clientId).id;

export const getPersistedQuestionsCount = createSelector(
  getItems,
  items => items.filter(item => item.rules.some(rule => !!rule.id)).length,
);

export const getSelectedQuestionIds = createSelector(getItems, items =>
  items.map(property('questionId')).filter(questionId => !!questionId),
);

export const isReceived = state => state.isReceived;

const reducer = handleActions(
  {
    [Constants.SET_POST_SURVEY_LOGIC]: (state, action) =>
      update(state, {
        $set: {
          items: action.payload,
          isReceived: true,
        },
      }),

    [Constants.ADD_POST_LOGIC_ITEM]: (state, action) =>
      update(state, {
        items: {
          $push: [action.payload],
        },
      }),

    [Constants.REMOVE_POST_LOGIC_ITEM]: (state, action) => {
      const itemId = action.payload;
      return update(state, {
        items: {
          $splice: [[getItemIndex(state, itemId), 1]],
        },
      });
    },

    [Constants.SET_POST_LOGIC_QUESTION_ID]: (state, action) => {
      const { itemId } = action.payload;

      return update(state, {
        items: {
          [getItemIndex(state, itemId)]: {
            questionId: {
              $set: action.payload.questionId,
            },
          },
        },
      });
    },

    [Constants.TOGGLE_POST_LOGIC_ITEM]: (state, action) => {
      const { itemId } = action.payload;

      return update(state, {
        items: {
          [getItemIndex(state, itemId)]: {
            isExpanded: {
              $apply: expanded => !expanded,
            },
          },
        },
      });
    },

    [Constants.ADD_POST_LOGIC_RULE]: (state, action) => {
      const { itemId } = action.payload;
      return update(state, {
        items: {
          [getItemIndex(state, itemId)]: {
            rules: {
              $push: [action.payload.rule],
            },
          },
        },
      });
    },

    [Constants.SET_POST_LOGIC_RULE]: (state, action) => {
      const { rule, itemId } = action.payload;
      const { clientId } = rule;

      return update(state, {
        items: {
          [getItemIndex(state, itemId)]: {
            rules: {
              [getRuleIndex(state, itemId, clientId)]: {
                $set: rule,
              },
            },
          },
        },
      });
    },

    [Constants.REMOVE_POST_LOGIC_RULE]: (state, action) => {
      const { itemId, clientId } = action.payload;

      return update(state, {
        items: {
          [getItemIndex(state, itemId)]: {
            rules: {
              $splice: [[getRuleIndex(state, itemId, clientId), 1]],
            },
          },
        },
      });
    },

    [Constants.RESET_POST_LOGIC_RULE]: (state, action) => {
      const { clientId, itemId } = action.payload;

      return update(state, {
        items: {
          [getItemIndex(state, itemId)]: {
            rules: {
              [getRuleIndex(state, itemId, clientId)]: {
                $set: {
                  clientId,
                },
              },
            },
          },
        },
      });
    },
  },
  initialState,
);

export default reducer;
